'use client';

import {
  ArrowLeft,
  Bell,
  CalendarDays,
  Clock12,
  CreditCard,
  FileText,
  Globe,
  HelpCircle,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Star,
  Wallet
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

import Logout from '@ui/components/icons/Logout';
import UserEdit from '@ui/components/icons/UserEdit';
import { Badge } from '@ui/components/ui/badge';
import { cn } from '@ui/lib/utils';
import { isFeatureEnabled } from '@website/config/featureFlags';
import { useCurrentProfessional } from '@website/hooks/useCurrentProfessional';
import { useCurrentUser, useUserType } from '@website/hooks/useCurrentUser';
import { useLogout } from '@website/hooks/useLogout';
import { useModeSwitcher } from '@website/hooks/useModeSwitcher';
import { useUserCTAs } from '@website/hooks/useUserCTAs';

export const ProfileSidebar = () => {
  const t = useTranslations('Profile') as any;
  const pathname = usePathname();
  const locale = useLocale();
  const { data: user } = useCurrentUser();
  const userType = useUserType();
  // Use the cookie-backed user type as a synchronous auth signal so we don't
  // flash the public sidebar for authenticated users on first paint (the
  // `['user']` query is async and returns `undefined` while loading).
  const isAuthenticated = !!user || userType !== 'public';

  const isProfessional = user?.isProfessional ?? userType === 'professional';

  // Unified settings CTA hook — same source of truth as the mobile menu and
  // the bottom-nav settings badge. We pull `payments-action` and
  // `profile-completion` from it; the Profile-row pill text/icon still
  // depends on the underlying mentor sub-state and is computed below.
  const ctas = useUserCTAs();
  const showPaymentsBadge = ctas.has('settings-payments-action');

  // Single source of truth for the owner view — returns status + revisions.
  // Routed through the shared hook so the key matches every other consumer;
  // otherwise invalidations from profile edits wouldn't reach this cache and
  // the sidebar badge would go stale.
  const { professional: profile } = useCurrentProfessional();

  const hasPendingRevision = profile?.hasPendingRevision ?? false;

  // `pending` (server-side review) drives the clock badge. `incomplete` is NOT
  // pending — it's the mentor's turn — so it gets its own "to complete" badge
  // instead of the ambiguous "under review" copy.
  const isUnderReview = hasPendingRevision || profile?.status === 'pending';
  const isIncomplete = profile?.status === 'incomplete' || profile?.status === 'rejected';
  // Only surface "Action required" when the incomplete profile was previously
  // declined — for a brand-new incomplete profile the "to complete" label is
  // more accurate than "action required".
  const needsActionAfterDecline = isIncomplete && !!profile?.rejectionReason;

  // Profile-row pill text — mirrors the mobile menu's precedence.
  const profileBadge = needsActionAfterDecline
    ? t('actionRequired')
    : isUnderReview
      ? t('underReview')
      : isIncomplete
        ? t('toComplete')
        : undefined;

  // Nudge the mentor to the completion checklist while their first approval is
  // still outstanding. The CTA hook owns this rule — kept as a local alias so
  // the JSX below reads the same as before the refactor.
  const needsCompletion = ctas.has('settings-profile-completion');

  const options = React.useMemo(() => {
    // Auth-only entries — hidden for public visitors landing on public pages
    // that share this layout (FAQ, Privacy Policy, Terms). Every target route
    // here is behind a requireAuth guard, so showing them without a session
    // just links to screens that immediately redirect to login.
    const authOnlyOptions = isAuthenticated
      ? [
          ...(needsCompletion
            ? [
                {
                  label: t('completeProfile'),
                  href: `/${locale}/settings/complete-profile`,
                  icon: <Sparkles className="size-5 text-amber-600 fill-amber-400" />
                }
              ]
            : []),
          {
            label: t('title'),
            href: `/${locale}/settings/profile`,
            icon: needsActionAfterDecline ? (
              <Bell className="size-5 text-error-600" />
            ) : isUnderReview ? (
              <Clock12 className="size-5 text-warning-600" />
            ) : isIncomplete ? (
              <Clock12 className="size-5 text-amber-600" />
            ) : (
              <UserEdit className="size-5 text-inherit" />
            ),
            badge: profileBadge,
            badgeDot: profileBadge === t('toComplete')
          },
          {
            label: t('notificationSettings'),
            href: `/${locale}/settings/notifications`,
            icon: <Bell className="size-5 text-inherit" />
          },
          // Calendar sync (export feed + external-calendar import). Import
          // blocks availability, which only professionals have. Mirrors the
          // mobile SettingsTabs entry so the feature is reachable on desktop.
          ...(isProfessional
            ? [
                {
                  label: t('calendarSync.title'),
                  href: `/${locale}/settings/calendar`,
                  icon: <CalendarDays className="size-5 text-inherit" />
                }
              ]
            : []),
          {
            label: t('savedAddress'),
            href: `/${locale}/settings/saved-addresses`,
            icon: <MapPin className="size-5 text-inherit" />
          },
          ...(isProfessional && isFeatureEnabled('MENTOR_SUBSCRIPTION')
            ? [
                {
                  label: t('subscription'),
                  href: `/${locale}/settings/subscription`,
                  icon: <RefreshCw className="size-5 text-inherit" />
                }
              ]
            : []),
          ...(isProfessional && isFeatureEnabled('MENTOR_SUBSCRIPTION')
            ? [
                {
                  label: t('transactions'),
                  href: `/${locale}/settings/transactions`,
                  icon: <Wallet className="size-5 text-inherit" />
                }
              ]
            : []),
          ...(isProfessional
            ? [
                {
                  label: t('payments'),
                  href: `/${locale}/settings/payments`,
                  icon: <CreditCard className="size-5 text-inherit" />,
                  badge: showPaymentsBadge ? t('toComplete') : undefined,
                  badgeDot: showPaymentsBadge
                }
              ]
            : []),
          ...(isProfessional && isFeatureEnabled('RATING')
            ? [
                {
                  label: t('myReviews'),
                  href: `/${locale}/settings/reviews`,
                  icon: <Star className="size-5 text-inherit" />
                }
              ]
            : []),
          {
            label: t('changeLanguage'),
            href: `/${locale}/settings/change-language`,
            icon: <Globe className="size-5 text-inherit" />
          }
        ]
      : [];

    const publicOptions = [
      {
        label: t('privacyPolicy'),
        href: `/${locale}/privacy-policy`,
        icon: <ShieldCheck className="size-5 text-inherit" />
      },
      {
        label: t('termsConditions'),
        href: `/${locale}/terms-conditions`,
        icon: <FileText className="size-5 text-inherit" />
      },
      {
        label: t('faq'),
        href: `/${locale}/faq`,
        icon: <HelpCircle className="size-5 text-inherit" />
      }
    ];

    return [...authOnlyOptions, ...publicOptions].map((option) => ({
      ...option,
      isActive: pathname.endsWith(option.href)
    }));
  }, [
    pathname,
    t,
    locale,
    isAuthenticated,
    isProfessional,
    showPaymentsBadge,
    isUnderReview,
    isIncomplete,
    needsActionAfterDecline,
    needsCompletion,
    profileBadge
  ]);

  const { logout } = useLogout();
  const modeSwitcher = useModeSwitcher(user ?? undefined);

  return (
    <div className="flex flex-col justify-center gap-2">
      {!isAuthenticated && (
        <ProfileTabItem
          option={{
            label: t('backToHome'),
            icon: <ArrowLeft className="size-5 text-inherit" />,
            href: `/${locale}`,
            isActive: false
          }}
          className="mb-2"
        />
      )}
      {options.map((option) => (
        <ProfileTabItem key={option.href} option={option} />
      ))}
      {isAuthenticated && (
        <>
          {/* Visually detach the mode switch from the settings list above so it
           * reads as an account-level action rather than "just another setting".
           * Keeps horizontal padding aligned with the rows so the rule spans the
           * same content width. */}
          <div className="border-t border-neutral-200 my-1 mx-3" />
          <ProfileTabItem
            option={{
              label: modeSwitcher.actionLabel,
              icon: <modeSwitcher.ActionIcon className="size-5 text-inherit" />,
              href: '/',
              isActive: false
            }}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              modeSwitcher.open();
            }}
          />
        </>
      )}
      {isAuthenticated && (
        <ProfileTabItem
          option={{
            label: t('logout'),
            icon: <Logout className="size-5 fill-error-600 " />,
            href: '/',
            isActive: false
          }}
          className="text-error-600"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            logout();
          }}
        />
      )}
      {modeSwitcher.dialogs}
    </div>
  );
};

const ProfileTabItem = ({
  option,
  className,
  onClick
}: {
  option: {
    label: string;
    icon: React.ReactNode;
    href: string;
    isActive: boolean;
    badge?: string;
    /** Pins a red corner dot on the badge — see SettingsTabItem.badgeDot. */
    badgeDot?: boolean;
    rightText?: string;
  };
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) => {
  return (
    <Link
      href={option.href}
      onClick={onClick}
      className={cn(
        `flex items-center gap-2 text-sm font-semibold px-3 py-3 text-neutral-700
            hover:bg-accent rounded-xs transition-colors duration-200 ease-in-out`,
        option.isActive && 'border-l-4 border-primary-500 rounded-xs bg-primary-50',
        className
      )}
      aria-current={option.isActive ? 'page' : undefined}
    >
      {option.icon}
      <span className="flex-1">{option.label}</span>
      {option.rightText && <span className="text-sm font-medium text-neutral-500">{option.rightText}</span>}
      {option.badge && (
        <span className="relative inline-flex">
          <Badge variant="secondary" className="text-xs">
            {option.badge}
          </Badge>
          {option.badgeDot && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-error-500 ring-2 ring-white"
            />
          )}
        </span>
      )}
    </Link>
  );
};
